// docs/61 EX13–EX14, docs/138 — exploration as the HUD and the board read it: how each of the
// thirty-two wonder looks is drawn (an Oryx frame, a tint, a stir), how each of the eight airs feels
// (its motes, its tint, its music), what a gift says, how a find and a camp are drawn, the briefing,
// the journal, and the words the call-outs show for each exploration effect. Pure functions over
// `Snapshot.explore` (the party's view: a wonder's words, omen and gift blank until beheld, a camp
// unnamed until seen).
import type { BoonName, CrawlLair, CrawlView, Effect, ExploreCamp, ExploreGift, ExploreVault, ExploreView, ExploreWonder, FixtureView, Snapshot, StashView, Tile } from '../sim/types';
import { BOON_WORDS } from '../crawl/crawl';

/** How a wonder moves while it stands on the board. */
export type Stir = 'breathe' | 'hover' | 'sway' | 'pulse' | 'still' | 'ripple' | 'turn';
export type WonderFamily = 'creature' | 'plant' | 'building' | 'object' | 'water' | 'stone' | 'light';

/**
 * One look (`fk_schema::WonderLook`, in its order). `frame` is the Oryx frame drawn, `tint` its
 * colouring, `scale` its size in squares; `extra` names a small vector touch drawn over it
 * (antlers on a pale beast, eyes on an owl or a toad, harp strings, a ring of toadstools…).
 */
export interface LookDef {
  key: string; name: string; family: WonderFamily; frame: string; tint?: number; alpha?: number; scale: number;
  stir: Stir; glow: number;
  /** Glass, ice or light: a bright copy of the frame laid over it in this colour. */
  sheen?: number;
  /** Drawn as a flat silhouette of this colour with its own frame faint over it (glass, ice). */
  fill?: number;
  extra?: 'antlers' | 'eyes' | 'ring' | 'lotus' | 'roses' | 'strings' | 'orbit' | 'beam' | 'tower' | 'cairn' | 'rune';
  /**
   * The commissioned piece (placeholder art, tools/puzzle-art.py) drawn instead where the atlas has
   * it: as it is, with no tint, sheen or `extra` (a beam still falls on it).
   */
  art?: string;
}

export const LOOKS: readonly LookDef[] = [
  // creatures
  { key: 'Stag', name: 'a stag', family: 'creature', frame: 'creature_229', tint: 0xe6f4ff, scale: 1.1, stir: 'breathe', glow: 0xcfeaff, sheen: 0x304860, extra: 'antlers', art: 'wonder-stag' },
  { key: 'Owl', name: 'an owl', family: 'creature', frame: 'creature_234', tint: 0xc89a62, scale: 1.0, stir: 'turn', glow: 0xffd88a, extra: 'eyes', art: 'wonder-owl' },
  { key: 'Fox', name: 'a fox', family: 'creature', frame: 'creature_225', tint: 0xff9a4a, scale: 0.95, stir: 'breathe', glow: 0xffb070, art: 'wonder-fox' },
  { key: 'Serpent', name: 'a serpent', family: 'creature', frame: 'creature_226', scale: 1.0, stir: 'sway', glow: 0x9dffb0, art: 'wonder-serpent' },
  { key: 'Moth', name: 'a moth', family: 'creature', frame: 'creature_227', tint: 0xfff0b8, scale: 1.05, stir: 'hover', glow: 0xfff3a0, art: 'wonder-moth' },
  { key: 'Heron', name: 'a heron', family: 'creature', frame: 'creature_232', tint: 0xdcecff, scale: 1.1, stir: 'turn', glow: 0xdff2ff, art: 'wonder-heron' },
  { key: 'Tortoise', name: 'a tortoise', family: 'creature', frame: 'creature_217', tint: 0x97a865, scale: 1.0, stir: 'breathe', glow: 0xc8f0a0, extra: 'eyes', art: 'wonder-tortoise' },
  { key: 'Wisp', name: 'a wisp', family: 'creature', frame: 'creature_361', scale: 0.9, alpha: 0.9, stir: 'hover', glow: 0x7ff6ff, art: 'wonder-wisp' },
  { key: 'Spirit', name: 'a spirit', family: 'creature', frame: 'creature_372', alpha: 0.8, scale: 1.05, stir: 'hover', glow: 0x9fd8ff, art: 'wonder-spirit' },
  { key: 'Toad', name: 'a toad', family: 'creature', frame: 'creature_218', tint: 0x9bcf62, scale: 0.85, stir: 'breathe', glow: 0xd8ff9a, extra: 'eyes', art: 'wonder-toad' },
  // plants
  { key: 'Bloom', name: 'a flower', family: 'plant', frame: 'world_0165', tint: 0xff92d6, scale: 1.0, stir: 'sway', glow: 0xffb8e8, art: 'wonder-bloom' },
  { key: 'MushroomRing', name: 'a ring of toadstools', family: 'plant', frame: 'item_094', scale: 0.42, stir: 'pulse', glow: 0xff9a8a, extra: 'ring', art: 'wonder-mushroom-ring' },
  { key: 'GlassTree', name: 'a strange tree', family: 'plant', frame: 'world_0275', alpha: 0.9, scale: 1.2, stir: 'sway', glow: 0xbff8ff, fill: 0xc4f2ff, sheen: 0x9fe8ff, art: 'wonder-glass-tree' },
  { key: 'Vine', name: 'hanging vines', family: 'plant', frame: 'world_0276', tint: 0x86d070, scale: 1.1, stir: 'sway', glow: 0xb8ff9a, art: 'wonder-vine' },
  { key: 'Lotus', name: 'a water flower', family: 'plant', frame: 'world_0112', scale: 1.0, stir: 'ripple', glow: 0xffc0e0, extra: 'lotus', art: 'wonder-lotus' },
  { key: 'Thornrose', name: 'a briar rose', family: 'plant', frame: 'world_0276', tint: 0x9a3c58, scale: 1.05, stir: 'sway', glow: 0xff8aa8, extra: 'roses', art: 'wonder-thornrose' },
  // buildings
  { key: 'Arch', name: 'an arch', family: 'building', frame: 'world_0259', scale: 1.1, stir: 'still', glow: 0xffe39a, art: 'wonder-arch' },
  { key: 'LoneDoor', name: 'a door standing alone', family: 'building', frame: 'world_0203', scale: 1.05, stir: 'still', glow: 0xffd27a, art: 'wonder-lone-door' },
  { key: 'Tower', name: 'a tower', family: 'building', frame: 'world_0218', tint: 0xd2c8b6, scale: 1.25, stir: 'still', glow: 0xffe08a, extra: 'tower', art: 'wonder-tower' },
  { key: 'Well', name: 'a well', family: 'building', frame: 'world_0548', scale: 1.05, stir: 'still', glow: 0x9fd8ff, art: 'wonder-well' },
  { key: 'Statue', name: 'a statue', family: 'building', frame: 'world_0376', scale: 1.1, stir: 'still', glow: 0xe8e2d0 },
  { key: 'Obelisk', name: 'an obelisk', family: 'building', frame: 'world_0219', tint: 0x9a96b0, scale: 1.2, stir: 'still', glow: 0xc8a8ff, extra: 'rune', art: 'wonder-obelisk' },
  // objects
  { key: 'Orrery', name: 'an orrery', family: 'object', frame: 'item_144', scale: 0.85, stir: 'pulse', glow: 0xffe08a, extra: 'orbit', art: 'wonder-orrery' },
  { key: 'Lantern', name: 'a lantern', family: 'object', frame: 'item_148', scale: 0.9, stir: 'pulse', glow: 0xffd060, art: 'wonder-lantern' },
  { key: 'Bell', name: 'a bell', family: 'object', frame: 'item_150', scale: 0.95, stir: 'sway', glow: 0xffe39a, art: 'bell' },
  { key: 'Mirror', name: 'a mirror', family: 'object', frame: 'item_066', scale: 1.0, stir: 'pulse', glow: 0xbfe8ff, art: 'wonder-mirror' },
  { key: 'Harp', name: 'a harp', family: 'object', frame: 'item_191', tint: 0xffd36a, scale: 1.0, stir: 'pulse', glow: 0xffe8a8, extra: 'strings', art: 'wonder-harp' },
  // waters, stones and lights
  { key: 'Pool', name: 'a still pool', family: 'water', frame: 'world_1737', tint: 0x9fd0ff, scale: 1.0, stir: 'ripple', glow: 0x9fe0ff, art: 'wonder-pool' },
  { key: 'Crystal', name: 'a crystal', family: 'stone', frame: 'world_0217', tint: 0xb8a4ff, scale: 1.05, stir: 'pulse', glow: 0xd0b8ff, sheen: 0x6a40c0, art: 'wonder-crystal' },
  { key: 'Cairn', name: 'a cairn', family: 'stone', frame: 'world_0618', tint: 0xe6e0d0, scale: 0.8, stir: 'still', glow: 0xf0e8c8, extra: 'cairn', art: 'cairn' },
  { key: 'Bones', name: 'great bones', family: 'stone', frame: 'world_0096', scale: 1.15, stir: 'still', glow: 0xf2ead2, art: 'wonder-bones' },
  { key: 'Moonbeam', name: 'a shaft of light', family: 'light', frame: 'item_101', tint: 0xf4f8ff, scale: 0.7, stir: 'pulse', glow: 0xf4f8ff, extra: 'beam', art: 'wonder-moonstone' },
];
export const lookOf = (look: number): LookDef => LOOKS[look] ?? LOOKS[0]!;

/** docs/61 EX13: one air (`fk_schema::Air`, in its order). `motes` names the drift the board shows. */
export interface AirDef {
  key: string; name: string; words: string;
  motes: 'mist' | 'gold' | 'dust' | 'dream' | 'pollen' | 'fireflies' | 'spores' | 'snow';
  /** The mote colours, picked among by each mote. */
  colours: number[];
  /** A wash over the board (colour and strength) that gives the place its light. */
  tint: number; wash: number;
  /** The music director's mood while the party explores. */
  mood: 'wonder' | 'mystery';
  /** The sky the module stamps for the air (docs/61 EX13), for boards that carry none (the gallery). */
  hour: number; weather: string;
}

export const AIRS: readonly AirDef[] = [
  { key: 'Mist', name: 'Mist', words: 'White mist on everything; shapes loom and go.', motes: 'mist', colours: [0xe8eef4, 0xdfe6ee], tint: 0xd8e2ec, wash: 0.12, mood: 'mystery', hour: 9, weather: 'fog:0.6:0.1' },
  { key: 'Glimmer', name: 'Glimmer', words: 'Sun through leaves, motes of gold, things that shine.', motes: 'gold', colours: [0xffe08a, 0xfff2c0, 0xffc860], tint: 0xffe6a8, wash: 0.07, mood: 'wonder', hour: 14, weather: 'clear' },
  { key: 'Hush', name: 'Hush', words: 'A held breath: no birds, no wind, every sound too loud.', motes: 'dust', colours: [0xd8dce4, 0xc8ccd6], tint: 0xb8c0d0, wash: 0.1, mood: 'mystery', hour: 17, weather: 'overcast:0.5' },
  { key: 'Dream', name: 'Dream', words: 'Wrong colours and slow time, a place half asleep.', motes: 'dream', colours: [0xd8a8ff, 0xffa8e0, 0xa8d8ff], tint: 0xc8a0ff, wash: 0.16, mood: 'wonder', hour: 21, weather: 'clear' },
  { key: 'Bloom', name: 'Bloom', words: 'Everything flowering at once; pollen, scent and bees.', motes: 'pollen', colours: [0xfff4a0, 0xffd0ec, 0xffffff], tint: 0xffe0f0, wash: 0.06, mood: 'wonder', hour: 11, weather: 'clear' },
  { key: 'Starlit', name: 'Starlit', words: 'Night with the stars close overhead; fireflies.', motes: 'fireflies', colours: [0xd8ff80, 0xfff080, 0xa0ffc0], tint: 0x5060a8, wash: 0.1, mood: 'wonder', hour: 23, weather: 'clear' },
  { key: 'Echoing', name: 'Echoing', words: 'Under ground: drips, far voices, sounds that come back changed.', motes: 'spores', colours: [0x8ff0e0, 0xa0c8ff], tint: 0x608098, wash: 0.08, mood: 'mystery', hour: 12, weather: 'clear' },
  { key: 'Rime', name: 'Rime', words: 'Frost on every edge, breath smoking, ice that sings.', motes: 'snow', colours: [0xffffff, 0xdff0ff], tint: 0xd0e8ff, wash: 0.1, mood: 'mystery', hour: 10, weather: 'frost:0.6' },
];
export const airOf = (air: number): AirDef => AIRS[air] ?? AIRS[0]!;

/** What a gift is, in a word (the journal) — `undefined` while it is not known. */
export function giftWord(g: ExploreGift): string | undefined {
  switch (g.kind) {
    case 'Marvel': return 'a marvel';
    case 'Boon': return `a boon — it ${BOON_WORDS[g.boon as BoonName]?.verb ?? 'blesses'}`;
    case 'Sight': return 'a vantage — the land around was shown';
    case 'Path': return 'the mist parted';
    case 'Trove': return 'it led to something hidden';
    case 'Lore': return 'a piece of the story';
    case 'Omen': return 'an omen — remember it';
    case 'Enchant': return 'a gift of enchantment — lay it on a piece of gear from the inventory';
    case 'Unknown': return undefined;
  }
}

/** A wonder's name as the board labels it: its own once seen, else its look's ("a stag"). */
export function wonderName(w: ExploreWonder | undefined, f?: FixtureView): string {
  if (w?.name) return w.name;
  if (f?.name) return f.name;
  return w ? lookOf(w.look).name : 'something strange';
}

/** The exploration's wonder standing on fixture `id`. */
export function wonderOf(x: ExploreView | undefined, id: number): { index: number; wonder: ExploreWonder } | undefined {
  const i = x?.wonders.findIndex((w) => w.fixture === id) ?? -1;
  return i < 0 || !x ? undefined : { index: i, wonder: x.wonders[i]! };
}

/** The door ids of the veils still lying across the trails, and every veil square (`x,y`). */
export function veilSquares(x: ExploreView | undefined): Set<string> {
  const out = new Set<string>();
  for (const v of x?.veils ?? []) if (!v.parted) for (const t of v.tiles) out.add(`${t.x},${t.y}`);
  return out;
}

/**
 * docs/138: how the party stands — the one wonder (unseen, seen or found, and its name once seen),
 * the finds picked up (and how many lie found but untaken), the camps seen and roused, the vaults
 * found and opened, lore read. `total` and `beheld` count every wonder (old boards held several).
 */
export interface ExploreProgress {
  wonder: 'unknown' | 'seen' | 'found'; wonderName: string; beheld: number; total: number; omens: number;
  finds: number; findsLying: number; camps: number; roused: number; vaults: number; vaultsOpened: number; lore: number; loreTotal: number;
}

/** How much the party has seen and done (docs/138 XO3: the wonder first, then the side objective). */
export function progress(snap: Pick<Snapshot, 'explore' | 'crawl'> & Partial<Pick<Snapshot, 'stashes' | 'fixtures' | 'board' | 'fog'>>): ExploreProgress {
  const x = snap.explore;
  const lore = snap.crawl?.lore ?? [];
  const ws = x?.wonders ?? [];
  const found = ws.find((w) => w.found);
  const seen = found ?? ws.find((w) => w.name);
  const finds = (snap.stashes ?? []).filter((f) => f.kind === 'Find');
  const camps = seenCamps(x, snap.crawl, snap.board?.w ?? 0);
  const vaults = seenVaults(snap);
  return {
    wonder: found ? 'found' : seen ? 'seen' : 'unknown',
    wonderName: seen ? wonderName(seen) : '',
    beheld: ws.filter((w) => w.found).length,
    total: ws.length,
    omens: ws.filter((w) => w.found && w.gift.kind === 'Omen' && w.omen).length,
    finds: finds.filter((f) => f.opened).length,
    findsLying: finds.filter((f) => !f.opened && !f.hidden).length,
    camps: camps.length,
    roused: camps.filter((c) => c.roused).length,
    vaults: vaults.length,
    vaultsOpened: vaults.filter((v) => v.opened).length,
    lore: lore.filter((l) => l.read).length,
    loreTotal: lore.length,
  };
}

/** One line of the journal, in the order the party found things. */
export interface JournalEntry { kind: 'wonder' | 'omen' | 'lore' | 'vault'; head: string; text: string; look?: number; index: number }

/** The journal: the wonder beheld (its words, then its omen apart), lore read, vaults found. */
export function journal(snap: Pick<Snapshot, 'explore' | 'crawl'> & Partial<Pick<Snapshot, 'stashes' | 'fixtures' | 'board' | 'fog'>>): JournalEntry[] {
  const out: JournalEntry[] = [];
  const x = snap.explore;
  (x?.wonders ?? []).forEach((w, i) => {
    if (!w.found) return;
    out.push({ kind: 'wonder', head: cap(wonderName(w)), text: w.text, look: w.look, index: i });
    if (w.gift.kind === 'Omen' && w.omen) out.push({ kind: 'omen', head: `Omen of ${wonderName(w)}`, text: w.omen, look: w.look, index: i });
  });
  (snap.crawl?.lore ?? []).forEach((l, i) => { if (l.read) out.push({ kind: 'lore', head: cap(l.title || 'A fragment'), text: l.text, index: i }); });
  for (const v of seenVaults(snap)) if (v.vault.text) out.push({ kind: 'vault', head: cap(v.vault.name || 'A sealed vault'), text: v.vault.text, index: v.index });
  return out;
}

// ---- docs/138 XO9: the briefing -------------------------------------------------------------------

/** The briefing's three parts, blank ones dropped (a vision from before docs/138 has none). */
export function briefParts(x: ExploreView | undefined): Array<{ key: 'why' | 'history' | 'ties'; head: string; text: string }> {
  const b = x?.brief;
  if (!b) return [];
  const parts = [
    { key: 'why' as const, head: 'Why you are here', text: b.why },
    { key: 'history' as const, head: 'What this place is', text: b.history },
    { key: 'ties' as const, head: 'How it ties in', text: b.ties },
  ];
  return parts.filter((p) => p.text.trim());
}

/** docs/138 XO4: the finders-keepers rule, said the same everywhere. */
export const FINDERS_KEEPERS = 'Finders keepers: what each hero picks up here is theirs alone. Experience is shared by everyone.';
/** docs/138 XO3: the side objective, said the same everywhere. */
export const SIDE_LINE = 'Side: search for hidden loot (Search shows hidden things within three squares).';

// ---- docs/138 XO7: finds ------------------------------------------------------------------------------

/** How a find looks (`look` index, as `fk_forge::explore::FIND_LOOKS` orders them): a frame, or drawn coins. */
export interface FindLook { key: string; name: string; frame?: string; tint?: number }
export const FIND_LOOKS: readonly FindLook[] = [
  { key: 'Purse', name: 'a purse', frame: 'item_072' },
  { key: 'Coins', name: 'some coins' },
  { key: 'Ring', name: 'a ring', frame: 'item_077' },
  { key: 'Trinket', name: 'a trinket', frame: 'item_023' },
  { key: 'Gear', name: 'a piece of gear', frame: 'item_199' },
];
export const findLookOf = (look: number | undefined): FindLook => FIND_LOOKS[(look ?? 0) % FIND_LOOKS.length] ?? FIND_LOOKS[0]!;

/** What a picked-up find rises as over the hero who took it: "+6 gold (yours)", "Silver ring (yours)". */
export function findTag(gold: number, items: readonly string[], personal: boolean): string {
  const parts = [...(gold > 0 ? [`+${gold} gold`] : []), ...items];
  const said = parts.length ? parts.join(' · ') : 'nothing';
  return personal ? `${said} (yours)` : said;
}

/** Is fixture `id` an exploration find (stash kind `Find`)? */
export const isFind = (stashes: readonly StashView[] | undefined, id: number): boolean => !!stashes?.some((s) => s.id === id && s.kind === 'Find');

// ---- docs/138 XO8: camps -----------------------------------------------------------------------------

/** `fk_schema::camps::CampDress`, in its order. */
export const CAMP_DRESS = ['Fire', 'Tents', 'Webs', 'Nest', 'Burrow', 'Bones', 'Totems', 'Wagons', 'Hive', 'Pool', 'Ruin', 'Altar'] as const;
export type CampDressKey = typeof CAMP_DRESS[number];
export const campDressOf = (dress: number): CampDressKey => CAMP_DRESS[dress] ?? 'Fire';

/**
 * The crawl lair a camp is: the party's view keeps only awake lairs (so `camp.lair`, an index into
 * the truth's list, can point past them); trust the index when that lair is the camp's (by name or
 * by the chamber its middle stands in), else find the awake lair that is.
 */
export function campLair(crawl: CrawlView | undefined, camp: ExploreCamp, w: number): CrawlLair | undefined {
  if (!crawl) return undefined;
  const area = crawl.areaOf[camp.at.y * w + camp.at.x];
  const mine = (l: CrawlLair | undefined) => !!l && ((!!camp.name && l.name === camp.name) || (area !== undefined && area > 0 && l.area === area - 1));
  const byIndex = crawl.lairs[camp.lair];
  if (mine(byIndex)) return byIndex;
  return crawl.lairs.find((l) => mine(l));
}

/** The camps the party has seen (named), each with whether it has been roused. */
export function seenCamps(x: ExploreView | undefined, crawl?: CrawlView, w = 0): Array<{ camp: ExploreCamp; index: number; roused: boolean }> {
  return (x?.camps ?? []).flatMap((camp, index) => (camp.name ? [{ camp, index, roused: !!campLair(crawl, camp, w)?.awake }] : []));
}

/** The units of awake lairs and engaged roamers: every other foe on an exploration's board is asleep in its camp. */
export function awakeFoes(crawl: CrawlView | undefined): Set<number> {
  return new Set<number>([...(crawl?.lairs ?? []).filter((l) => l.awake).flatMap((l) => l.units), ...(crawl?.roamers ?? []).filter((r) => r.engaged).map((r) => r.unit)]);
}

// ---- docs/138 XO6: vaults ----------------------------------------------------------------------------

/** The vaults whose stair the party has found on the board, and whether each chest is open. */
export function seenVaults(snap: Partial<Pick<Snapshot, 'explore' | 'stashes' | 'fixtures' | 'board' | 'fog'>>): Array<{ vault: ExploreVault; index: number; opened: boolean }> {
  const x = snap.explore;
  if (!x) return [];
  const fog = snap.fog;
  const seen = (t: Tile) => !fog || (fog.cells[t.y * fog.w + t.x] ?? 0) !== 0;
  return x.vaults.flatMap((vault, index) => {
    const stair = snap.fixtures?.find((f) => f.id === vault.stair);
    if (!stair || !seen(stair.tile)) return [];
    const chest = snap.stashes?.find((s) => s.id === vault.chest);
    return [{ vault, index, opened: !!chest?.opened }];
  });
}

/** How an exploration moment is shown: a discovery card, a call-out over the board, the leaving card. */
export type ExploreCalloutKind = 'wonder' | 'veil' | 'vantage' | 'way' | 'depart';
export interface ExploreCallout { kind: ExploreCalloutKind; head: string; line: string; eyebrow?: string; look?: number; tile?: Tile; unit?: number }

/**
 * The card an exploration entry makes (undefined for any other entry). `x` should be the board
 * after the pulse: the party's view carries a wonder's words only once it has been beheld.
 */
export function exploreCallout(e: Effect, x: ExploreView | undefined, name: (unit: number) => string | undefined): ExploreCallout | undefined {
  const who = (u: number | undefined) => (u !== undefined ? name(u) : undefined);
  switch (e.kind) {
    case 'Discovered': {
      const w = x?.wonders[e.wonder];
      const by = who(e.unit);
      const gift = w ? giftWord(w.gift) : undefined;
      const eyebrow = `${by ? `${by} beholds` : 'Beheld'}${gift && w?.gift.kind !== 'Marvel' ? ` · ${gift}` : ''}`;
      const line = [w?.text ?? '', w?.gift.kind === 'Omen' && w.omen ? `Omen: “${w.omen}”` : '', 'The wonder is found. Every hero may Behold it once for a blessing.'].filter(Boolean).join(' ');
      return { kind: 'wonder', head: cap(wonderName(w)), line, eyebrow, ...(w ? { look: w.look } : {}), unit: e.unit };
    }
    case 'VeilParted': {
      const v = x?.veils[e.veil];
      return { kind: 'veil', head: v?.name ? `${cap(v.name)} parts` : 'The mist parts', line: 'A way opens where the mist lay.', ...(v?.tiles[0] ? { tile: v.tiles[0] } : {}), ...(e.by !== undefined ? { unit: e.by } : {}) };
    }
    case 'Glimpsed': {
      const by = who(e.unit);
      return { kind: 'vantage', head: 'The land opens out', line: e.cells > 0 ? `${by ?? 'The party'} sees far from here: ${e.cells} squares go on the map.` : 'Nothing new to see from here.', tile: e.at, unit: e.unit };
    }
    case 'WaySighted': {
      const w = x?.ways[e.way];
      return { kind: 'way', head: w?.name ? cap(w.name) : 'A way out', line: w?.glimpse ?? '', eyebrow: 'A way on into the mist', ...(w?.tiles[0] ? { tile: w.tiles[0] } : {}), unit: e.by };
    }
    case 'Departed': {
      const w = x?.ways[e.way];
      const line = [w?.ending ?? '', x?.ending ?? ''].filter(Boolean).join(' ');
      return { kind: 'depart', head: w?.name ? `On down ${w.name}` : 'The party goes on', line, eyebrow: 'Into the mist' };
    }
    default: return undefined;
  }
}

/** docs/138 XO5: the tag over a hero the wonder blesses. */
export const BLESSED_TAG = 'BLESSED';

/** True for the effects exploration adds to the log (docs/61). */
export function isExploreEffect(e: Effect): e is Extract<Effect, { kind: 'Discovered' | 'VeilParted' | 'Glimpsed' | 'WaySighted' | 'Departed' }> {
  return e.kind === 'Discovered' || e.kind === 'VeilParted' || e.kind === 'Glimpsed' || e.kind === 'WaySighted' || e.kind === 'Departed';
}

/**
 * docs/61 EX13: the music while exploring — the air's own mood, and tension while a camp (docs/138
 * XO8) is roused and fighting.
 */
export function exploreMood(snap: Pick<Snapshot, 'explore' | 'crawl' | 'units'>): 'wonder' | 'mystery' | 'tension' | undefined {
  if (!snap.explore) return undefined;
  const awake = awakeFoes(snap.crawl);
  const fighting = snap.units.some((u) => u.faction === 'enemy' && u.onBoard !== false && !u.downed && u.hp > 0 && awake.has(u.unitId));
  return fighting ? 'tension' : airOf(snap.explore.air).mood;
}

/** A deterministic 0..1 from a square and a salt (decoration that every client draws alike). */
export function hash01(x: number, y: number, salt = 0): number {
  const h = Math.imul(x * 73856093 ^ y * 19349663 ^ salt * 83492791, 0x9e3779b1) >>> 0;
  return (h % 10007) / 10007;
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
